import React, { FunctionComponent, useMemo } from 'react'

import LottieView from 'lottie-react-native'
import styled from 'styled-components/native'

import spinnerAnimation from '../../assets/lottie/loadingspinner.json'
import { tID } from '../../utils/utils'

const Container = styled.View<{ size: number | string }>(({ size }) => ({
  height: `${size}px`,
  width: `${size}px`,
}))

export interface LoadingSpinnerProps {
  size?: string | number
  colorValueHex?: string
}

type LottieRGBA = [number, number, number, number]
type FillNode = { ty: 'fl'; c: { k: LottieRGBA; [key: string]: unknown }; [key: string]: unknown }

const hexToLottieColor = (hex: string): LottieRGBA => {
  const cleaned = hex.replace('#', '')
  const full =
    cleaned.length === 3
      ? cleaned
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned
  const r = parseInt(full.slice(0, 2), 16) / 255
  const g = parseInt(full.slice(2, 4), 16) / 255
  const b = parseInt(full.slice(4, 6), 16) / 255
  return [r, g, b, 1]
}

const isFillNode = (node: Record<string, unknown>): node is FillNode =>
  node.ty === 'fl' && typeof node.c === 'object' && node.c !== null

// Deep-clones a Lottie JSON tree, replacing every fill shape's color (`c.k`) with `rgba`.
// Needed because lottie-react-native drops `colorFilters` on web (see issue #1266 — the web
// renderer doesn't forward it to dotlottie-react), so we mutate the source JSON instead.
const recolorFills = <T,>(node: T, rgba: LottieRGBA): T => {
  if (Array.isArray(node)) {
    return node.map((item) => recolorFills(item, rgba)) as T
  }
  if (node && typeof node === 'object') {
    const obj = node as Record<string, unknown>
    if (isFillNode(obj)) {
      return { ...obj, c: { ...obj.c, k: rgba } } as T
    }
    const next: Record<string, unknown> = {}
    for (const key of Object.keys(obj)) {
      next[key] = recolorFills(obj[key], rgba)
    }
    return next as T
  }
  return node
}

export const LoadingSpinner: FunctionComponent<LoadingSpinnerProps> = ({ size = 24, colorValueHex }) => {
  const source = useMemo(() => {
    if (!colorValueHex) return spinnerAnimation
    return recolorFills(spinnerAnimation, hexToLottieColor(colorValueHex))
  }, [colorValueHex])
  return (
    <Container size={size} testID={tID('LoadingSpinner')}>
      <LottieView source={source} loop autoPlay style={{ width: size, height: size }} />
    </Container>
  )
}
